import { createContext, useContext, useState, useEffect } from 'react';
import { api, setAuthToken, clearAuthToken, getAuthToken } from '../lib/api';
import { track } from '../lib/analytics';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getAuthToken();
    if (token) {
      // Has localStorage token — use it
      api.get('/me').then(res => {
        setUser(res.data.user);
      }).catch(() => {
        clearAuthToken();
      }).finally(() => setLoading(false));
    } else {
      // No localStorage token — try cookie-based session (first-party Google OAuth)
      api.get('/me', { withCredentials: true }).then(res => {
        if (res.data.user) {
          setUser(res.data.user);
        }
      }).catch(() => {
        // No valid session
      }).finally(() => setLoading(false));
    }
  }, []);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    setAuthToken(res.data.token);
    setUser(res.data.user);
    track('login', { method: 'email' });
    return res.data;
  };

  const register = async (data) => {
    const res = await api.post('/auth/register', data);
    setAuthToken(res.data.token);
    setUser(res.data.user);
    track('signup', { method: 'email' });
    return res.data;
  };

  // For Google OAuth callback — sets token and user in one call
  const loginWithToken = (token, userData) => {
    setAuthToken(token);
    setUser(userData);
  };

  // Force re-fetch user from API (useful after OAuth)
  const refreshUser = async () => {
    const token = getAuthToken();
    if (token) {
      try {
        const res = await api.get('/me');
        setUser(res.data.user);
        return res.data.user;
      } catch {
        clearAuthToken();
        setUser(null);
      }
    }
    return null;
  };

  const logout = async () => {
    track('logout');
    clearAuthToken();
    try { await api.post('/auth/logout', {}, { withCredentials: true }); } catch {}
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, loginWithToken, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
